import type { ComponentRef } from '@angular/core';
import { computed, inject, Injectable, Injector, type Signal, signal } from '@angular/core';
import type { PWAInstallStatus } from './type/pwa-install-status';
import type { Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { isInstalledPWA } from './is-installed-pwa';
import { canInstallPWA } from './can-install-pwa';
import type { BeforeInstallPromptEvent } from './type/before-install-prompt-event';
import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { assertIsNotNil } from '@pct-ts/assert';
import { ComponentPortal } from '@angular/cdk/portal';
import { PwaInstallOverlayPanelComponent } from './pwa-install-overlay-panel.component';
import { UAParser } from 'ua-parser-js';

const LOCALSTORAGE_KEY = 'HasUserBeenShownSubscriptionInstallPopup';

@Injectable({
  providedIn: 'root',
})
export class InstallableService {
  readonly #injector = inject(Injector);
  readonly #overlay = inject(Overlay);
  readonly #window = inject(DOCUMENT).defaultView;

  #deferredPrompt: BeforeInstallPromptEvent | null = null;
  #overlayRef: OverlayRef | null = null;

  readonly #installable = signal<PWAInstallStatus>(
    // ['macos', 'ios'].includes((UAParser(window.navigator.userAgent).os.name ?? '').toLowerCase())
    // eslint-disable-next-line sonarjs/no-nested-conditional
    isInstalledPWA() ? 'INSTALLED' : canInstallPWA() ? 'SUPPORTED' : 'DETECT',
  );
  readonly #installed = computed(() => this.#installable() === 'INSTALLED');
  get installable(): Signal<PWAInstallStatus> {
    return this.#installable.asReadonly();
  }
  get installable$(): Observable<PWAInstallStatus> {
    return toObservable(this.installable);
  }

  constructor() {
    if (this.#installed()) {
      return;
    }

    if (this.#installable() === 'SUPPORTED' && !this.#checkHasUserBeenShownSubscriptionPopup()) {
      setTimeout(() => this.#openPopup(), 10 * 1000);
    }

    this.#checkHasSupport();
  }

  async installApp(): Promise<void> {
    if (this.#installed()) {
      return;
    }

    if (
      ['macos', 'ios'].includes((UAParser(window.navigator.userAgent).os.name ?? '').toLowerCase())
    ) {
      this.#installable.set('DETECT');
      return;
    }

    if (this.#deferredPrompt !== null) {
      await this.#deferredPrompt.prompt();
      const { outcome } = await this.#deferredPrompt.userChoice;
      this.#deferredPrompt = null;

      if (outcome === 'dismissed') {
        this.#installable.set('DENIED');
        return;
      }

      this.#installable.set('INSTALLED');
    }
  }

  #checkHasSupport(): void {
    if (this.installable() === 'SUPPORTED') {
      window.addEventListener('beforeinstallprompt', e => {
        e.preventDefault();
        this.#deferredPrompt = e as BeforeInstallPromptEvent;
      });
    }
  }

  #checkHasUserBeenShownSubscriptionPopup(): boolean {
    assertIsNotNil(this.#window, 'Window not found');
    const result = this.#window.localStorage.getItem(LOCALSTORAGE_KEY);
    if (result === null) {
      return false;
    }
    try {
      return JSON.parse(result) ?? false;
    } catch {
      return false;
    }
  }

  #setUserHasSeenThePopup(): void {
    assertIsNotNil(this.#window, 'Window not found');
    this.#window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(true));
  }

  #openPopup(): void {
    const { device } = UAParser(window.navigator.userAgent);

    const positionStrategy =
      device.is('mobile') || device.is('tablet')
        ? this.#overlay.position().global().centerHorizontally().bottom('16px')
        : this.#overlay.position().global().left('16px').bottom('16px');

    this.#overlayRef = this.#overlay.create({
      positionStrategy,
      hasBackdrop: false,
      panelClass: ['z-[1000]'],
      disposeOnNavigation: false,
      scrollStrategy: this.#overlay.scrollStrategies.reposition(),
    });

    const portal = new ComponentPortal(PwaInstallOverlayPanelComponent, null, this.#injector);

    const ref = this.#overlayRef.attach(portal) as ComponentRef<PwaInstallOverlayPanelComponent>;
    ref.instance.close.subscribe(() => {
      this.#setUserHasSeenThePopup();
      this.#closeOverlay();
    });
  }

  #closeOverlay(): void {
    if (this.#overlayRef !== null) {
      this.#overlayRef.dispose();
      this.#overlayRef = null;
    }
  }
}
